import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StudiesApiService, StudySearchFilters, StudyDetails } from '../../../services/studies-api.service';

/**
 * Test component for verifying StudiesApiService integration with BFF endpoints.
 * 
 * This component provides a UI to manually test:
 * - searchStudies() - Search for studies with various filters
 * - getStudy() - Retrieve detailed study information
 * - downloadStudy() - Download study as ZIP file
 * 
 * Usage:
 * 1. Ensure BFF server is running at http://localhost:3000
 * 2. Navigate to /studies/test-integration
 * 3. Use the test controls to verify each endpoint
 */
@Component({
    selector: 'app-bff-integration-test',
    templateUrl: './bff-integration-test.component.html',
    styleUrls: ['./bff-integration-test.component.scss'],
    standalone: false
})
export class BffIntegrationTestComponent implements OnInit {
  // Search test state
  searchFilters: StudySearchFilters = {
    patientName: '*Doe*',
    pageSize: 10
  };
  searchResults: any = null;
  isSearching = false;
  searchError: string | null = null;

  // Study details test state
  studyInstanceUid = '1.2.840.113619.2.55.3.1234567890';
  studyDetails: StudyDetails | null = null;
  isLoadingStudy = false;
  studyError: string | null = null;

  // Download test state
  downloadStudyUidsInput: string = '';
  isDownloading = false;
  downloadError: string | null = null;

  constructor(
    private studiesApi: StudiesApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    console.log('BFF Integration Test Component initialized');
    console.log('BFF Base URL:', 'http://localhost:3000/api/v1');
  }

  /**
   * Test searchStudies() method
   * Verifies that the service correctly calls the BFF /studies endpoint
   */
  async testSearch() {
    this.isSearching = true;
    this.searchError = null;
    this.searchResults = null;

    try {
      console.log('Testing searchStudies() with filters:', this.searchFilters);
      
      const response = await firstValueFrom(this.studiesApi.searchStudies(this.searchFilters));
      
      console.log('Search response received:', response);
      this.searchResults = response;
      
      this.snackBar.open(
        `Search successful! Found ${response?.items?.length || 0} studies.`,
        'OK',
        { duration: 3000 }
      );
    } catch (error: any) {
      console.error('Search test error:', error);
      this.searchError = this.extractErrorMessage(error);
      
      this.snackBar.open(
        `Search failed: ${this.searchError}`,
        'Close',
        { duration: 5000 }
      );
    } finally {
      this.isSearching = false;
    }
  }

  /**
   * Test getStudy() method
   * Verifies that the service correctly calls the BFF /studies/:studyInstanceUid endpoint
   */
  async testGetStudy() {
    if (!this.studyInstanceUid || !this.studyInstanceUid.trim()) {
      this.snackBar.open('Please enter a StudyInstanceUID', 'OK', { duration: 2000 });
      return;
    }

    this.isLoadingStudy = true;
    this.studyError = null;
    this.studyDetails = null;

    try {
      console.log('Testing getStudy() with UID:', this.studyInstanceUid);
      
      const study = await firstValueFrom(this.studiesApi.getStudy(this.studyInstanceUid));
      
      console.log('Study details received:', study);
      this.studyDetails = study;
      
      this.snackBar.open(
        `Study details retrieved successfully! Found ${study?.series?.length || 0} series.`,
        'OK',
        { duration: 3000 }
      );
    } catch (error: any) {
      console.error('Get study test error:', error);
      this.studyError = this.extractErrorMessage(error);
      
      this.snackBar.open(
        `Get study failed: ${this.studyError}`,
        'Close',
        { duration: 5000 }
      );
    } finally {
      this.isLoadingStudy = false;
    }
  }

  /**
   * Test downloadStudy() method
   * Verifies that the service correctly calls the BFF /exports/studies endpoint
   * and handles the ZIP file download
   */
  async testDownload() {
    // Parse UIDs from input (split by newline) or use study details/input
    let uidsToDownload: string[] = [];
    
    if (this.downloadStudyUidsInput.trim()) {
      uidsToDownload = this.downloadStudyUidsInput
        .split('\n')
        .map(uid => uid.trim())
        .filter(uid => uid.length > 0);
    } else if (this.studyDetails?.studyInstanceUid) {
      uidsToDownload = [this.studyDetails.studyInstanceUid];
    } else if (this.studyInstanceUid.trim()) {
      uidsToDownload = [this.studyInstanceUid.trim()];
    }

    if (uidsToDownload.length === 0) {
      this.snackBar.open('Please provide at least one StudyInstanceUID to download', 'OK', { duration: 2000 });
      return;
    }

    this.isDownloading = true;
    this.downloadError = null;

    try {
      console.log('Testing downloadStudy() with UIDs:', uidsToDownload);
      
      const blob = await firstValueFrom(this.studiesApi.downloadStudy(uidsToDownload));
      
      console.log('Download blob received:', blob);
      console.log('Blob size:', blob?.size, 'bytes');
      console.log('Blob type:', blob?.type);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = uidsToDownload.length === 1 
        ? `study-${uidsToDownload[0]}.zip`
        : `studies-export-${new Date().getTime()}.zip`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      const fileSizeKB = ((blob?.size || 0) / 1024).toFixed(2);
      const fileSizeMB = ((blob?.size || 0) / (1024 * 1024)).toFixed(2);
      const sizeDisplay = (blob?.size || 0) > 1024 * 1024 
        ? `${fileSizeMB} MB` 
        : `${fileSizeKB} KB`;
      
      this.snackBar.open(
        `Download successful! File size: ${sizeDisplay}`,
        'OK',
        { duration: 3000 }
      );
    } catch (error: any) {
      console.error('Download test error:', error);
      this.downloadError = this.extractErrorMessage(error);
      
      this.snackBar.open(
        `Download failed: ${this.downloadError}`,
        'Close',
        { duration: 5000 }
      );
    } finally {
      this.isDownloading = false;
    }
  }

  /**
   * Extract error message from HTTP error response
   */
  private extractErrorMessage(error: any): string {
    if (error?.error?.error) {
      return error.error.error;
    } else if (error?.error && typeof error.error === 'string') {
      return error.error;
    } else if (error?.message) {
      return error.message;
    } else if (error?.status) {
      return `HTTP ${error.status}: ${error.statusText || 'Unknown error'}`;
    }
    return 'Unknown error occurred';
  }

  /**
   * Update download UIDs from search results
   */
  useSearchResultForDownload() {
    if (this.searchResults?.items && this.searchResults.items.length > 0) {
      const uids = this.searchResults.items
        .slice(0, 5) // Limit to first 5 for testing
        .map((item: any) => item.studyInstanceUid);
      this.downloadStudyUidsInput = uids.join('\n');
      this.snackBar.open(
        `Loaded ${uids.length} StudyInstanceUIDs from search results`,
        'OK',
        { duration: 2000 }
      );
    }
  }

  /**
   * Use study details UID for download
   */
  useStudyDetailsForDownload() {
    if (this.studyDetails?.studyInstanceUid) {
      this.downloadStudyUidsInput = this.studyDetails.studyInstanceUid;
      this.snackBar.open('Loaded StudyInstanceUID from study details', 'OK', { duration: 2000 });
    }
  }

  /**
   * Clear all test results
   */
  clearResults() {
    this.searchResults = null;
    this.studyDetails = null;
    this.searchError = null;
    this.studyError = null;
    this.downloadError = null;
    this.downloadStudyUidsInput = '';
  }
}
